import type React from 'react'
import styles from './ReviewStep.module.css'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {Tooltip} from 'pepsico-ds'

interface ReviewAccordionChildrenSectionProps {
  type: 'build' | 'repo' | 'deploy' | 'qa' | 'security' | 'appsec' | 'feature'
  data: any
  [key: string]: any
}

const ReviewAccordionChildrenSection: React.FC<ReviewAccordionChildrenSectionProps> = ({
  type,
  data,
  buildDurationThreshold,
}) => {
  // Defensive: if data is not an array or is empty/null, show '-'
  if (!Array.isArray(data) || data.length === 0) return <div>-</div>

  // Helper to safely render a value (string/number only)
  function safeVal(val: any) {
    if (val == null) return '-'
    if (typeof val === 'string' || typeof val === 'number') return val
    if (typeof val === 'object' && typeof val.label === 'string') return val.label
    if (typeof val === 'object' && typeof val.id === 'string') return val.id
    return '-'
  }

  // Helper to render rows based on a config
  function renderRows(dataArr: any[], rowConfigs: {label: string; value: (item: any) => any}[][]) {
    return dataArr.length > 0 ? (
      dataArr.map((item, idx) => (
        <div key={idx} className={sharedStyles.selfOnboardingWrapper}>
          <div className={styles.contentGroupContainer}>
            {rowConfigs.map((fields, rowIdx) => (
              <div key={rowIdx} className={styles.contentGroupRow}>
                {fields.map((field, colIdx) => (
                  <div key={colIdx} className={styles.contentGroup}>
                    <div className={styles.rowMetaData}>{field.label}</div>
                    <Tooltip
                      direction="top-middle"
                      style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                      text={String(safeVal(field.value(item)))}
                    >
                      <div className={styles.rowData}>{safeVal(field.value(item))}</div>
                    </Tooltip>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))
    ) : (
      <div>-</div>
    )
  }

  if (type === 'build') {
    const pipelines = data
    const rows = []
    if (buildDurationThreshold) {
      rows.push(
        <div key={'duration-threshold'} className={styles.contentGroupRow}>
          <div className={styles.contentGroup}>
            <div className={styles.rowMetaData}>Duration Threshold</div>
            <div className={styles.rowData}>{safeVal(buildDurationThreshold)}</div>
          </div>
        </div>,
      )
    }

    // Next rows: 2 pipeline jobs per row (starting from index 1)
    for (let i = 0; i < pipelines.length; i += 2) {
      rows.push(
        <div key={`pipeline-row-${i}`} className={styles.contentGroupRow}>
          <div className={styles.contentGroup}>
            <div className={styles.rowMetaData}>Job</div>
            <Tooltip
              direction="top-middle"
              style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
              text={String(safeVal(pipelines[i]?.buildJob))}
            >
              <div className={styles.rowData}>{safeVal(pipelines[i]?.buildJob)}</div>
            </Tooltip>
          </div>
          {pipelines[i + 1] && (
            <div className={styles.contentGroup}>
              <div className={styles.rowMetaData}>Job</div>
              <Tooltip
                direction="top-middle"
                style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                text={String(safeVal(pipelines[i + 1]?.buildJob))}
              >
                <div className={styles.rowData}>{safeVal(pipelines[i + 1]?.buildJob)}</div>
              </Tooltip>
            </div>
          )}
        </div>,
      )
    }
    return <>{rows}</>
  }

  if (type === 'repo') {
    // Special: custom row logic for repo/branches
    const repos = data
    return repos.length > 0 ? (
      repos.map((repo: any, idx: number) => {
        const branches = repo.branches || []
        const rows = [
          <div key={repo.repoUrl + '-url-branch0'} className={styles.contentGroupRow}>
            <div className={styles.contentGroup}>
              <div className={styles.rowMetaData}>URL</div>
              <Tooltip
                direction="top-right"
                style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                text={String(safeVal(repo.repoUrl))}
              >
                <div className={styles.rowData}>{safeVal(repo.repoUrl)}</div>
              </Tooltip>
            </div>
            {branches[0] !== undefined && (
              <div className={styles.contentGroup}>
                <div className={styles.rowMetaData}>Branch 1</div>
                <Tooltip
                  direction="top-middle"
                  style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                  text={String(safeVal(branches[0]))}
                >
                  <div className={styles.rowData}>{safeVal(branches[0])}</div>
                </Tooltip>
              </div>
            )}
          </div>,
        ]
        for (let i = 1; i < branches.length; i += 2) {
          rows.push(
            <div key={repo.repoUrl + '-branches-' + i} className={styles.contentGroupRow}>
              <div className={styles.contentGroup}>
                <div className={styles.rowMetaData}>{`Branch ${i + 1}`}</div>
                <Tooltip
                  direction="top-right"
                  style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                  text={String(safeVal(branches[i]))}
                >
                  <div className={styles.rowData}>{safeVal(branches[i])}</div>
                </Tooltip>
              </div>
              {branches[i + 1] !== undefined && (
                <div className={styles.contentGroup}>
                  <div className={styles.rowMetaData}>{`Branch ${i + 2}`}</div>
                  <Tooltip
                    direction="top-middle"
                    style={{whiteSpace: 'nowrap', maxWidth: 'fit-content'}}
                    text={String(safeVal(branches[i + 1]))}
                  >
                    <div className={styles.rowData}>{safeVal(branches[i + 1])}</div>
                  </Tooltip>
                </div>
              )}
            </div>,
          )
        }
        return (
          <div key={idx} className={styles.contentGroupContainer}>
            {rows}
          </div>
        )
      })
    ) : (
      <div>-</div>
    )
  }

  // All other types use row config
  const rowConfigMap: Record<string, {label: string; value: (item: any) => any}[][]> = {
    deploy: [
      [
        {label: 'Job', value: d => d.deployJob},
        {label: 'Prod Branch', value: d => d.deployProdBranch},
      ],
      [
        {label: 'Hotfix Branch', value: d => d.deployHotfixBranch},
        {label: 'Prod Env Suffix', value: d => d.deployProdEnvSuffix},
      ],
    ],
    qa: [
      [
        {label: 'ADO Board', value: q => q.adoBoard},
        {label: 'Build Pipeline', value: q => q.buildPipeline},
      ],
    ],
    security: [
      [
        {label: 'Static Analysis Job', value: s => s.staticAnalysisJob},
        {label: 'Sonar Branch', value: s => s.sonarBranch},
      ],
    ],
    appsec: [[{label: 'URL', value: a => a.url}]],
    feature: [
      [
        {label: 'Team Name', value: f => f.teamName},
        {label: 'On Time Delivery (%)', value: f => f.onTimeDelivery},
      ],
      [
        {label: 'Commitment Met (%)', value: f => f.commitmentMet},
        {label: 'Velocity (Story Points)', value: f => f.velocity},
      ],
      [
        {label: 'Defect Density', value: f => f.defectDensity},
        {label: 'Automation Efficiency (%)', value: f => f.automationEfficiency},
      ],
      [
        {label: 'Automation Coverage Target', value: f => f.testAutomation},
        {label: 'Overall Automation Coverage Target', value: f => f.overallAutomationCoverage},
      ],
      [{label: 'Program Tags', value: f => f.programTags}],
      [
        {label: 'Reusability Tags', value: f => f.reusabilityTags},
        {label: 'Reusability Baseline Period', value: f => f.reusabilityBaselinePeriod},
      ],
      [
        {label: 'Global Story Points', value: f => f.reusableStoryPoints},
        {label: 'Total Story Points', value: f => f.totalStoryPoints},
      ],
      [
        {label: 'Baseline Period', value: f => f.automatedBaselinePeriod},
        {label: 'Automated Test Cases', value: f => f.automatedTestCases},
      ],
      [{label: 'Total Test Cases', value: f => f.totalTestCases}],
    ],
  }

  if (rowConfigMap[type]) {
    return renderRows(data, rowConfigMap[type])
  }

  return <div>-</div>
}

export default ReviewAccordionChildrenSection
